/**
 * AppLayout — wires AppShell with the customer-portal nav config, auth,
 * account scope and the staff "view as" state. Rendered inside ProtectedRoute.
 *
 * Staff sessions must pick a portal user before anything renders: without a
 * target the layout redirects to the picker instead of showing an unscoped
 * (all-customers) portal. A `?as_user=` still in the address bar means the
 * ViewAsProvider is about to consume it, so we hold on a spinner rather than
 * bouncing to the picker.
 */
import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppShell, locationIdFromPath, useDarkMode } from '@pazportal/ui';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import { readViewAsParamFromUrl, STAFF_VIEW_AS_PATH, useViewAs } from '../contexts/ViewAsContext';
import { useLocations } from '../hooks/useLocations';
import { OPEN_TICKET_STATUSES, useTickets } from '../hooks/useTickets';
import { locationNavSections, navSections, PORTAL_COLOR, PORTAL_NAME, PORTAL_SUBTITLE } from '../config/navigation';
import { pickViewBrand, sidebarIsLight } from '../config/brandThemes';
import { resolveOwnerBrand } from '../config/ownerBrands';
import { useI18n } from '../i18n/I18nProvider';
import type { MessageKey } from '../i18n/messages';
import { OnboardingModal } from './OnboardingModal';
import { AccountSwitcher } from './AccountSwitcher';
import { LocationSwitcher } from './LocationSwitcher';
import { dedupeAccountsByName } from '../lib/portfolio';
import { ViewAsBanner } from './ViewAsBanner';
import { LanguageSwitcher } from './LanguageSwitcher';
import { NotificationBell } from './NotificationBell';
import { CommandPalette, type CommandItem } from './CommandPalette';
import { CustomerInvitations } from './CustomerInvitations';

const SECTION_TO_KEY: Record<string, MessageKey> = {
  Overview: 'nav.section.overview',
  Site: 'nav.section.site',
  Support: 'nav.section.support',
  Account: 'nav.section.account',
};
const LABEL_TO_KEY: Record<string, MessageKey> = {
  Dashboard: 'nav.dashboard',
  Locations: 'nav.locations',
  Trends: 'nav.trends',
  Tickets: 'nav.tickets',
  Documents: 'nav.documents',
  Billing: 'nav.billing',
  Network: 'nav.network',
  Services: 'nav.services',
  Systems: 'nav.systems',
  Reports: 'nav.reports',
  Activity: 'nav.activity',
  'All locations': 'nav.all_locations',
};

function initialsOf(value: string | null | undefined): string {
  if (!value) return '··';
  const words = value.trim().split(/\s+/).filter(Boolean);
  const letters = words.length >= 2 ? words[0][0] + words[1][0] : value.slice(0, 2);
  return letters.toUpperCase();
}

export function AppLayout() {
  const { user, role: jwtRole, signOut } = useAuth();
  const { isStaff, viewAsUserId } = useViewAs();
  const { accounts, activeAccount, isPortfolio, activeRole, loading: accountsLoading } = useAccount();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle } = useDarkMode();
  const { t } = useI18n();
  const { data: locationsData } = useLocations();
  const { data: openTickets } = useTickets({ status: OPEN_TICKET_STATUSES });

  // Hotel-brand theme: a property page always wears that property's brand
  // (also under "All accounts", the default landing state); otherwise a single
  // selected account wears its brand; portfolio-wide views have none.
  const brandTheme = useMemo(
    () =>
      pickViewBrand({
        pathname: location.pathname,
        isPortfolio,
        locations: locationsData?.locations ?? [],
        activeAccountName: activeAccount?.name,
      }),
    [location.pathname, isPortfolio, locationsData, activeAccount],
  );
  useEffect(() => {
    const root = document.documentElement;
    if (brandTheme) root.setAttribute('data-brand', brandTheme.key);
    else root.removeAttribute('data-brand');
    return () => root.removeAttribute('data-brand');
  }, [brandTheme]);
  // Brand sidebar styling is light-mode only (the style sheets are light palettes);
  // in dark mode the sidebar keeps the PazNetworks ink and the brand header is hidden.
  const sidebarBrand = brandTheme && !isDark ? brandTheme : null;
  // Portfolio-owner mark (Stanford) on the user icon — white mark on dark sidebars,
  // full-colour mark when a brand makes the sidebar light.
  const ownerBrand = useMemo(() => resolveOwnerBrand(accounts.map((a) => a.name)), [accounts]);
  const ownerMark = ownerBrand
    ? sidebarBrand && sidebarIsLight(sidebarBrand.theme)
      ? ownerBrand.logo
      : ownerBrand.logoOnDark
    : undefined;

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');

  // ⌘K / Ctrl+K anywhere except while typing into a field.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'k' || !(event.metaKey || event.ctrlKey)) return;
      const target = event.target as HTMLElement | null;
      const typing =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable === true;
      if (typing) return;
      event.preventDefault();
      setPaletteQuery('');
      setPaletteOpen(true);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const paletteItems: CommandItem[] = useMemo(() => {
    const pages: CommandItem[] = navSections.flatMap((section) =>
      section.items.map((item) => ({
        id: `page:${item.href}`,
        kind: 'page' as const,
        label: LABEL_TO_KEY[item.label] ? t(LABEL_TO_KEY[item.label]) : item.label,
        hint: item.href,
        run: () => navigate(item.href),
      })),
    );

    const sites: CommandItem[] = (locationsData?.locations ?? []).map((site) => ({
      id: `site:${site.id}`,
      kind: 'site' as const,
      label: site.name,
      hint: [site.directory?.code, site.directory?.city ?? site.address?.city]
        .filter(Boolean)
        .join(' · '),
      // Aliases are searchable so an operations code finds its property.
      keywords: [site.directory?.code, ...(site.directory?.aliases ?? [])].filter(Boolean).join(' '),
      run: () => navigate(`/locations/${site.id}`),
    }));

    const tickets: CommandItem[] = (openTickets ?? []).map((ticket) => ({
      id: `ticket:${ticket.id}`,
      kind: 'ticket' as const,
      label: ticket.title,
      hint: [ticket.ticket_number, ticket.location?.name].filter(Boolean).join(' · '),
      keywords: ticket.ticket_number ?? '',
      run: () => navigate(`/tickets/${ticket.id}`),
    }));

    const actions: CommandItem[] = [
      {
        id: 'action:new-ticket',
        kind: 'action' as const,
        label: t('palette.action_new_ticket'),
        hint: t('nav.tickets'),
        run: () => navigate('/tickets?new=1'),
      },
      {
        id: 'action:trends',
        kind: 'action' as const,
        label: t('palette.action_trends'),
        hint: t('nav.trends'),
        run: () => navigate('/trends'),
      },
    ];

    return [...sites, ...tickets, ...pages, ...actions];
  }, [locationsData, navigate, openTickets, t]);

  if (isStaff && !viewAsUserId) {
    if (readViewAsParamFromUrl()) {
      return (
        <div className="flex min-h-[var(--paz-vh,100vh)] items-center justify-center bg-surface">
          <div className="flex flex-col items-center gap-3">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-sm font-semibold text-primary">{t('viewas.loading')}</span>
          </div>
        </div>
      );
    }
    return <Navigate to={STAFF_VIEW_AS_PATH} replace />;
  }

  const inViewAs = isStaff && !!viewAsUserId;
  const role = activeRole ?? jwtRole ?? 'viewer';
  const siteId = locationIdFromPath(location.pathname);
  const site = locationsData?.locations.find((item) => item.id === siteId);
  const locationCount = locationsData?.locations.length;
  const openCount = (siteId
    ? openTickets?.filter((ticket) => ticket.location_id === siteId)
    : openTickets
  )?.length ?? 0;
  const activeNav = siteId ? locationNavSections(siteId) : navSections;

  const propertyName = isPortfolio
    ? t('account.all_count', { n: accounts.length })
    : activeAccount?.name ?? (accountsLoading ? t('shell.loading') : t('account.none'));
  const propertySubtitle =
    locationCount === undefined
      ? t('shell.loading')
      : locationCount === 1
        ? t('shell.location_count_one')
        : t('shell.locations_count', { n: locationCount });

  const dynamicSections = activeNav.map((section) => ({
    ...section,
    label: SECTION_TO_KEY[section.label] ? t(SECTION_TO_KEY[section.label]) : section.label,
    items: section.items.map((item) => {
      const label = LABEL_TO_KEY[item.label] ? t(LABEL_TO_KEY[item.label]) : item.label;
      if (item.label === 'Tickets') {
        return {
          ...item,
          label,
          badge: openCount,
          badgeVariant: openCount > 0 ? ('warning' as const) : undefined,
          badgeLabel: t('nav.badge_open'),
        };
      }
      return { ...item, label };
    }),
  }));

  return (
    <>
      {!inViewAs && accounts.length > 0 && <OnboardingModal />}
      <AppShell
        navSections={dynamicSections}
        portalColor={sidebarBrand?.theme.sidebarBackground ?? PORTAL_COLOR}
        portalName={PORTAL_NAME}
        portalSubtitle={PORTAL_SUBTITLE}
        propertyName={site?.name ?? propertyName}
        propertySubtitle={
          site
            ? [site.address?.city, site.address?.state].filter(Boolean).join(', ') || propertySubtitle
            : propertySubtitle
        }
        propertyInitials={site ? initialsOf(site.name) : isPortfolio ? String(accounts.length) : initialsOf(activeAccount?.name)}
        userName={user?.email ?? ''}
        userRole={role}
        userInitials={initialsOf(user?.email)}
        brandHeader={
          sidebarBrand
            ? {
                logoSrc: sidebarBrand.theme.sidebarLogo,
                logoAlt: sidebarBrand.key.charAt(0).toUpperCase() + sidebarBrand.key.slice(1),
                title: sidebarBrand.title,
              }
            : isPortfolio && ownerBrand
              ? {
                  // Portfolio view has no single hotel brand, so the owner's
                  // mark heads the sidebar instead (Paul's brand themes still
                  // take over as soon as one property is selected).
                  logoSrc: ownerBrand.logoOnDark,
                  logoAlt: ownerBrand.name,
                  title: t('account.all_count', { n: dedupeAccountsByName(accounts).length }),
                }
              : undefined
        }
        // The PazNetworks logo is white, so it only replaces the text name while
        // the sidebar is dark; a brand theme that lightens the sidebar keeps
        // the text.
        portalLogoSrc={sidebarBrand && sidebarIsLight(sidebarBrand.theme) ? undefined : '/logo.svg'}
        accountControl={<AccountSwitcher variant="compact" />}
        userAvatarSrc={ownerMark}
        userAvatarAlt={ownerBrand?.name}
        currentPath={location.pathname}
        onNavigate={(href: string) => navigate(href)}
        onSignOut={signOut}
        onDarkModeToggle={toggle}
        isDarkMode={isDark}
        viewAsBanner={inViewAs ? <ViewAsBanner /> : undefined}
        headerActions={
          <>
            <NotificationBell />
            <LanguageSwitcher />
          </>
        }
        onGlobalSearch={(query) => {
          setPaletteQuery(query);
          setPaletteOpen(true);
        }}
      >
        {(inViewAs || accounts.length > 0) && <AccountSwitcher />}
        {!isStaff && <CustomerInvitations />}
        {siteId && <LocationSwitcher locationId={siteId} />}
        {(inViewAs || accounts.length > 0) && <Outlet />}
      </AppShell>
      <CommandPalette
        open={paletteOpen}
        items={paletteItems}
        initialQuery={paletteQuery}
        onClose={() => setPaletteOpen(false)}
      />
    </>
  );
}
